'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { type ChangeEvent, useCallback, useEffect, useMemo, useRef, useState, useTransition } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from '@/components/toast';
import { Spinner } from '@/components/ui/spinner';
import { loginWithPhone, login } from '../actions';
import BackgroundVideo from '@/components/BackgroundVideo';
import { useSession } from 'next-auth/react';
import { checkEmailDomain, type EmailDomainSuggestion } from '@/utils/emailDomainValidator';
import useIsWebview from '@/hooks/use-is-webview';
import { PierreRiveIdle } from '@/components/pierre-rive-idle';
import { Eye, EyeOff, Mail, MessageCircle } from 'lucide-react';
import { EmailDomainAutocomplete } from '@/components/email-domain-autocomplete';
type LoginMethod = 'phone' | 'email';
import useRudderStackAnalytics from '@/hooks/useRudderAnalytics';
import { useFirstTouchUTM } from '@/hooks/use-first-touch-utm';
import { useIfpCampaignParams } from '@/hooks/use-ifp-campaign-params';
import { mergeTrackingParams } from '@/lib/utm-tracker';
import { getSafeRedirectUrl } from '@/lib/utils';
import { sanitizeBrazilianPhone } from '@/utils/formatPhoneNumber';
import { loginErrorCodeFromStatus } from '@/lib/analytics/login-error-codes';
import { resolvePrefillEmail, setPrefillEmail, setResetPasswordFlowHandle } from '@/lib/auth/prefill-email';
import { usePageTransition } from '@/hooks/use-page-transition';
import { SocialAuthButtons } from '@/components/auth-social-buttons';
export default function LoginClient() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const callbackUrl = searchParams.get('callbackUrl');
  const [loginMethod, setLoginMethod] = useState<LoginMethod>('phone');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [isResetRequesting, setIsResetRequesting] = useState(false);
  const [isLoggingIn, startLoggingIn] = useTransition();
  const analytics = useRudderStackAnalytics();
  const {
    data: session,
    update
  } = useSession();
  const [isWebView] = useIsWebview();
  const firstTouchUTM = useFirstTouchUTM();
  const ifpCampaignParams = useIfpCampaignParams();
  const hasTrackedPageView = useRef(false);
  const isResetRequestingRef = useRef(false);
  const [emailSuggestion, setEmailSuggestion] = useState<EmailDomainSuggestion | null>(null);
  const hasShownSuggestion = useRef(false);
  const handleEmailBlur = useCallback(() => {
    if (!email) {
      setEmailSuggestion(null);
      return;
    }
    const suggestion = checkEmailDomain(email);
    setEmailSuggestion(suggestion.hasTypo ? suggestion : null);
  }, [email]);
  const acceptEmailSuggestion = useCallback(() => {
    if (emailSuggestion?.suggested) {
      setEmail(emailSuggestion.suggested);
      setEmailSuggestion(null);
    }
  }, [emailSuggestion]);
  usePageTransition();
  useEffect(() => {
    const prefill = resolvePrefillEmail(searchParams.get('email'));
    if (prefill) {
      if (prefill.includes('@')) {
        setEmail(prefill);
        setLoginMethod('email');
      } else {
        setPhoneNumber(prefill);
        setLoginMethod('phone');
      }
    }
  }, [searchParams]);

  // Handle WebView redirect for deleted accounts and session expiry - notify native app
  useEffect(() => {
    const error = searchParams.get('error');
    const callbackUrl_0 = searchParams.get('callbackUrl');
    if (isWebView && typeof window !== 'undefined' && (window as any).ReactNativeWebView) {
      // Account deleted, user not found, or exchange token errors - force logout
      if (error === 'account_deleted' || error === 'user_not_found' || error === 'no_token' || error === 'invalid_token' || error === 'exchange_failed') {
        (window as any).ReactNativeWebView.postMessage(JSON.stringify({
          type: 'LOGOUT'
        }));
      }
      // Session expired - redirect means session issue, no explicit error
      else if (callbackUrl_0 && !error) {
        (window as any).ReactNativeWebView.postMessage(JSON.stringify({
          type: 'SESSION_EXPIRED',
          payload: {
            callbackUrl: callbackUrl_0
          }
        }));
      }
    }
  }, [isWebView, searchParams]);
  useEffect(() => {
    if (!analytics || hasTrackedPageView.current) return;
    hasTrackedPageView.current = true;
    analytics.page('', {
      page_name: 'Login',
      platform: isWebView ? 'app' : 'web',
      is_logged_in: !!session?.user?.id,
      ...(firstTouchUTM || {})
    });
  }, [analytics, isWebView, session?.user?.id, firstTouchUTM]);
  const handleChangePhoneNumber = (e: ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value.length > 11) {
      value = value.slice(0, 11);
    }
    if (value.length > 2) {
      value = `(${value.slice(0, 2)}) ${value.slice(2)}`;
    }
    if (value.length > 10) {
      value = `${value.slice(0, 10)}-${value.slice(10)}`;
    }
    setPhoneNumber(value);
  };
  const handleChangeEmail = (e_0: ChangeEvent<HTMLInputElement>) => {
    setEmail(e_0.target.value);
    setEmailSuggestion(null);
    hasShownSuggestion.current = false;
  };
  const handleLoginResult = useCallback(async (result: {
    status: 'idle' | 'in_progress' | 'success' | 'failed' | 'invalid_data' | 'rate_limited';
    retryAfter?: number;
    user?: {
      id: string;
      name: string;
      email?: string;
      phone?: string;
    };
    subscription?: {
      hasActiveSubscription: boolean;
      subscriptionType?: string;
      billingPeriod?: string;
    };
    homePage?: string;
  }, method: 'email' | 'phone', identify: {
    email?: string;
    phone?: string;
  }) => {
    const trackLoginFailed = (errorMessage: string) => {
      analytics?.track('Login Failed', {
        login_method: method,
        platform: isWebView ? 'app' : 'web',
        timestamp: new Date().toLocaleString('pt-BR'),
        error: loginErrorCodeFromStatus(result.status),
        error_message: errorMessage
      });
    };
    if (result.status === 'rate_limited') {
      toast({
        type: 'error',
        description: 'Muitas tentativas. Tente novamente mais tarde.'
      });
      return;
    }
    if (result.status === 'failed') {
      toast({
        type: 'error',
        description: 'Dados de acesso incorretos.'
      });
      trackLoginFailed('Dados de acesso incorretos.');
      return;
    }
    if (result.status === 'invalid_data') {
      toast({
        type: 'error',
        description: 'Dados inválidos. Tente novamente.'
      });
      trackLoginFailed('Dados inválidos. Tente novamente.');
      return;
    }
    if (result.status === 'success') {
      const firstName = result.user?.name?.split(' ')[0];
      toast({
        type: 'success',
        description: firstName ? `Bem vindo de volta, ${firstName}!` : 'Login realizado com sucesso!'
      });
      if (result.user?.id) {
        await analytics?.identify(result.user.id, {
          name: result.user.name,
          email: identify.email,
          phone: identify.phone,
          createdAt: new Date().toLocaleString('pt-BR'),
          timestamp: new Date().toISOString(),
          plan: result.subscription?.subscriptionType ?? 'basic',
          has_active_subscription: result.subscription?.hasActiveSubscription ?? false,
          last_login: new Date().toISOString(),
          ...mergeTrackingParams(firstTouchUTM, ifpCampaignParams)
        });
      }
      analytics?.track('Login Successful', {
        login_method: method,
        platform: isWebView ? 'app' : 'web',
        timestamp: new Date().toLocaleString('pt-BR')
      });
      const redirectUrl = getSafeRedirectUrl(callbackUrl, '/chat');
      // Re-seed the root SessionProvider's client state immediately. signIn()
      // set the session cookie but did not touch the provider's React state,
      // and router.push() is a soft nav — the root layout never re-runs on
      // client navigation, so auth() isn't re-called. Without this, every
      // useSession() consumer on the target route sees a stale null session
      // for up to 5 min (refetchInterval), which is what bounced the
      // /my-account subscribe flow to /register (PIERRE-1603). This is the
      // root-cause fix and replaces the per-route SessionProvider workaround.
      //
      // try/finally: if the refetch rejects (e.g. transient network), still
      // navigate — the session cookie is already set by signIn(), so the
      // provider refreshes via refetchInterval/refetchOnWindowFocus. Avoids
      // leaving the user stuck on the login page.
      try {
        await update();
      } finally {
        router.push(redirectUrl);
      }
    }
  }, [analytics, isWebView, router, callbackUrl, firstTouchUTM, ifpCampaignParams, update]);
  const handleForgotPassword = useCallback(async () => {
    let identifier = loginMethod === 'phone' ? phoneNumber : email.trim();
    if (loginMethod === 'phone') {
      const formattedPhone = sanitizeBrazilianPhone(phoneNumber);
      if (!formattedPhone) {
        toast({
          type: 'error',
          description: 'Número de telefone inválido. Use o formato (DDD) 9XXXX-XXXX.'
        });
        return;
      }
      identifier = formattedPhone;
    } else if (!/^\S+@\S+\.\S+$/.test(identifier)) {
      toast({
        type: 'error',
        description: 'Digite um e-mail válido para continuar.'
      });
      return;
    }
    if (isResetRequestingRef.current) return;
    isResetRequestingRef.current = true;
    setIsResetRequesting(true);
    try {
      const response = await fetch('/api/mobile/auth/password-reset/otp/request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          identifier,
          channel: 'whatsapp'
        })
      });
      const result_0 = await response.json();
      if (!response.ok || !result_0?.data?.flowHandle) throw new Error();
      setPrefillEmail(identifier);
      setResetPasswordFlowHandle(result_0.data.flowHandle);
      router.push('/reset-password/confirm');
    } catch {
      toast({
        type: 'error',
        description: 'Não foi possível solicitar o código. Tente novamente.'
      });
    } finally {
      isResetRequestingRef.current = false;
      setIsResetRequesting(false);
    }
  }, [email, loginMethod, phoneNumber, router]);
  const handleLoginSubmit = useCallback(async () => {
    let formattedPhone_0: string | null = null;
    if (loginMethod === 'phone') {
      if (!phoneNumber) {
        toast({
          type: 'error',
          description: 'Preencha o número de telefone para continuar'
        });
        return;
      }
      formattedPhone_0 = sanitizeBrazilianPhone(phoneNumber);
      if (!formattedPhone_0) {
        toast({
          type: 'error',
          description: 'Número de telefone inválido. Use o formato (DDD) 9XXXX-XXXX.'
        });
        return;
      }
    } else {
      if (!email) {
        toast({
          type: 'error',
          description: 'Preencha o email para continuar'
        });
        return;
      }

      // First submit with a typo: show the suggestion and stop. A second
      // submit allows the user to keep the address they entered.
      const suggestion_0 = checkEmailDomain(email);
      if (suggestion_0.hasTypo && !hasShownSuggestion.current) {
        setEmailSuggestion(suggestion_0);
        hasShownSuggestion.current = true;
        return;
      }
    }
    if (!password) {
      toast({
        type: 'error',
        description: 'Digite sua senha para continuar'
      });
      return;
    }
    const formData = new FormData();
    if (loginMethod === 'phone' && formattedPhone_0) {
      formData.append('phoneNumber', formattedPhone_0);
      formData.append('password', password);
      startLoggingIn(async () => {
        const result_1 = await loginWithPhone({
          status: 'idle'
        }, formData);
        await handleLoginResult(result_1, 'phone', {
          email: result_1.user?.email,
          phone: formattedPhone_0
        });
      });
    } else {
      formData.append('email', email);
      formData.append('password', password);
      startLoggingIn(async () => {
        const result_2 = await login({
          status: 'idle'
        }, formData);
        await handleLoginResult(result_2, 'email', {
          email,
          phone: result_2.user?.phone
        });
      });
    }
  }, [password, loginMethod, phoneNumber, email, handleLoginResult]);
  const buttonContent = useMemo(() => {
    if (isLoggingIn) {
      return <div className="flex items-center gap-2">
          <Spinner size="lg" spinnerClassName="bg-black md:bg-white md:dark:bg-black" />
          <span>Entrando...</span>
        </div>;
    }
    return 'Continuar';
  }, [isLoggingIn]);
  return <div className="min-h-screen w-full flex bg-white dark:bg-black">
      <div className="w-full lg:flex-1 flex flex-col lg:p-16">
        <div className="flex-1 flex flex-col justify-center px-4 py-8 gap-8 max-w-md mx-auto w-full">
          <div className="flex flex-col gap-7 md:gap-6">
            <div className="flex flex-col gap-3 text-center md:text-left">
              <div className="flex justify-center md:justify-start mb-2">
                <div className="border-[0.5px] border-white/20 dark:border-white/20 rounded-xl bg-transparent">
                  <PierreRiveIdle className="size-[56px]" />
                </div>
              </div>

              <div className="flex flex-col md:items-start items-center gap-2">
                <h2 className="text-primary text-3xl lg:text-4xl font-bold">
                  Bem vindo de volta
                </h2>
                <span className="text-muted-foreground text-base">
                  Qual o seu número de Whatsapp
                </span>
              </div>
            </div>

            <div className="space-y-4">
              {/* Phone Number Input */}
              {loginMethod === 'phone' && <div className="relative flex gap-4">
                  <div className="h-14 select-none opacity-50 cursor-not-allowed px-4 gap-2 py-3 text-lg rounded-xl border-input bg-transparent flex justify-center items-center w-fit border ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-colors">
                    <Image src="/images/brasil-flag.svg" alt="bandeira do Brasil" width={25} height={25} />
                    <span className="text-muted-foreground">+55</span>
                  </div>

                  <Input name="phoneNumber" autoComplete="phoneNumber" value={phoneNumber} onChange={handleChangePhoneNumber} inputSize="xl" rounded="2xl" variant="outline" inputMode="numeric" maxLength={15} floatingLabel label="Número do WhatsApp" />
                </div>}

              {/* Email Input */}
              {loginMethod === 'email' && <div>
                  <div className="relative">
                    <Input type="email" name="email" autoComplete="off" value={email} onChange={handleChangeEmail} onBlur={handleEmailBlur} inputSize="xl" rounded="2xl" variant="outline" floatingLabel label="Digite seu email" className="pr-12" />
                    <EmailDomainAutocomplete email={email} onSelect={completed => {
                  setEmail(completed);
                  setEmailSuggestion(null);
                }} visible />
                  </div>
                  {emailSuggestion && <button type="button" onClick={acceptEmailSuggestion} className="mt-1 pl-4 text-sm text-muted-foreground hover:text-primary transition-colors text-left w-full">
                      Você quis dizer:{' '}
                      <span className="font-semibold text-primary underline">
                        {emailSuggestion.suggested}
                      </span>
                      ?
                    </button>}
                </div>}

              <div className="space-y-2">
                <div className="relative">
                  <Input type={passwordVisible ? 'text' : 'password'} name="password" autoComplete="current-password" value={password} onChange={e_1 => setPassword(e_1.target.value)} inputSize="xl" rounded="2xl" variant="outline" className="pr-12" label="Digite sua senha" floatingLabel onKeyDown={e_2 => {
                  if (e_2.key === 'Enter') {
                    handleLoginSubmit();
                  }
                }} />
                  <button type="button" onClick={() => setPasswordVisible(!passwordVisible)} className="absolute top-1/2 right-4 -translate-y-1/2 text-primary/50 hover:text-primary transition-colors">
                    {passwordVisible ? <EyeOff className="size-5" /> : <Eye className="size-5" />}
                  </button>
                </div>

                <div className="flex">
                  <button type="button" onClick={handleForgotPassword} disabled={isResetRequesting} className="text-sm text-muted-foreground transition-colors hover:text-[#a3ff12] disabled:cursor-not-allowed disabled:opacity-50">
                    {isResetRequesting ? 'Enviando código...' : 'Esqueceu a senha?'}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="w-full space-y-3">
            <Button type="button" rounded="full" className="w-full" onClick={handleLoginSubmit} size="xl" disabled={isLoggingIn}>
              {buttonContent}
            </Button>

            <Button asChild rounded="full" className="w-full bg-black text-white hover:bg-black/90 dark:border dark:border-white/20 dark:bg-black dark:text-white" size="xl">
              <Link href="/register">Criar conta</Link>
            </Button>

            <div className="relative flex items-center justify-center">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-muted-foreground/20" />
              </div>
              <div className="relative bg-white dark:bg-black px-4">
                <span className="text-xs text-muted-foreground uppercase">
                  ou
                </span>
              </div>
            </div>

            <Button type="button" variant="outline" rounded="full" className="w-full" size="xl" onClick={() => {
            setLoginMethod(loginMethod === 'phone' ? 'email' : 'phone');
            setPhoneNumber('');
            setEmail('');
            setPassword('');
            setPasswordVisible(false);
          }}>
              {loginMethod === 'phone' ? <Mail className="size-5" /> : <MessageCircle className="size-5" />}
              {loginMethod === 'phone' ? 'Logar com E-mail' : 'Logar com WhatsApp'}
            </Button>

            <SocialAuthButtons fallbackUrl={callbackUrl || '/login'} />

            <div className="text-center">
              <p className="text-muted-foreground text-sm">
                Ao continuar você concorda com os{' '}
                <Link href="/privacy-policy" className="underline hover:text-[#a3ff12] transition-colors">
                  Termos de uso
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="hidden lg:block w-[800px] max-w-[850px] m-3 lg:w-[55%] relative">
        <BackgroundVideo src="/videos/background.webm" className="rounded-3xl" />
      </div>
    </div>;
}